import { useCallback, useContext, useMemo, useSyncExternalStore } from 'react';

import type { BannerStateInfo } from './BannerStateContainer.ts';
import { BannerContext } from './BannerContext.tsx';
import {
	getFloatingBanners,
	hideFloatingBanner,
	showFloatingBanner,
	subscribeToFloatingBanners,
} from './floatingBannerStore.ts';

const EMPTY_FLOATING_BANNERS: readonly BannerStateInfo[] = Object.freeze([]);

export function useFloatingBannerHeight(): number {
	const { suppressed } = useContext(BannerContext);
	const getSnapshot = useCallback(
		() => getFloatingBanners().reduce((height, banner) => height + banner.height, 0),
		[],
	);
	const height = useSyncExternalStore(subscribeToFloatingBanners, getSnapshot, getSnapshot);
	return suppressed ? 0 : height;
}

export function useFloatingBannerEntries(): readonly BannerStateInfo[] {
	const { suppressed } = useContext(BannerContext);
	const getSnapshot = useCallback(
		() => (suppressed ? EMPTY_FLOATING_BANNERS : getFloatingBanners()),
		[suppressed],
	);
	return useSyncExternalStore(subscribeToFloatingBanners, getSnapshot, getSnapshot);
}

export function useFloatingBannerHeightBefore(name: string): number {
	const { suppressed } = useContext(BannerContext);
	const getSnapshot = useCallback(() => {
		if (suppressed) {
			return 0;
		}
		const banners = getFloatingBanners();
		const index = banners.findIndex((banner) => banner.name === name);
		return index === -1
			? 0
			: banners.slice(0, index).reduce((height, banner) => height + banner.height, 0);
	}, [name, suppressed]);
	return useSyncExternalStore(subscribeToFloatingBanners, getSnapshot, getSnapshot);
}

export type FloatingBannerActions = {
	showFloating: (name: string, height: number) => void;
	hideFloating: (name: string) => void;
};

/** Write-only access to the global store, without a legacy-container subscription. */
export function useFloatingBannerActions(): FloatingBannerActions {
	const { suppressed } = useContext(BannerContext);
	return useMemo(
		() => ({
			showFloating: (name: string, height: number) => {
				if (!suppressed) {
					showFloatingBanner(name, height);
				}
			},
			hideFloating: (name: string) => {
				if (!suppressed) {
					hideFloatingBanner(name);
				}
			},
		}),
		[suppressed],
	);
}
